import type { AppContext } from 'types/common.js'
import { getDefaultDateRange, type Filters } from '../home'
import { graphql } from '$houdini'
import { getSession } from 'stores/session'
import component from './PurchaseOrder.svelte'
import { stringToBoolean as toBool } from '@isoftdata/utility-string'

export interface PurchaseOrderFilters extends Filters {
	showDoneReceiving: boolean
	showPricesFinalized?: boolean
}

const purchaseOrderQuery = graphql(`
	query HomePurchaseOrders($filter: PurchaseOrderFilter) {
		purchaseOrders(filter: $filter) {
			items {
				doneReceiving
				finalized
				total
				receivedPercent
				date
				store {
					id
					code
					name
				}
				salesPerson {
					name
				}
				internalComments
				type
				purchaseOrderId
				vendor {
					name
				}
				totalQuantityReceived
			}
		}
	}
`)

const defaults = graphql(`
	query HomePurchaseOrderDefaults {
		settingValues {
			home {
				defaultPurchaseOrderHistoryDateRange
			}
		}
	}
`)

export default function ({ stateRouter }: AppContext) {
	stateRouter.addState({
		name: 'app.home.purchase-order',
		route: 'purchase-order',
		querystringParameters: ['fromDate', 'toDate', 'userId', 'storeId', 'showDoneReceiving', 'showPricesFinalized'],
		defaultParameters: {
			userId: () => getSession()?.userAccountId?.toString() ?? null,
			storeId: () => getSession()?.currentStore?.toString() ?? null,
			showDoneReceiving: 'false',
			showPricesFinalized: 'false',
		},
		template: {
			svelte: true,
			component,
		},
		async resolve(data, parameters) {
			if (parameters.fromDate === undefined && parameters.toDate === undefined) {
				const res = await defaults.fetch()
				const defaultDateRange = getDefaultDateRange(
					res?.data?.settingValues?.home?.defaultPurchaseOrderHistoryDateRange,
				)

				throw {
					redirectTo: {
						name: null,
						params: {
							fromDate: defaultDateRange.from,
							toDate: defaultDateRange.to,
						},
					},
				}
			}

			const filters: PurchaseOrderFilters = {
				userId: parameters.userId ? parseInt(parameters.userId, 10) || undefined : undefined,
				storeId: parameters.storeId ? parseInt(parameters.storeId, 10) || undefined : undefined,
				showDoneReceiving: toBool(parameters.showDoneReceiving),
				showPricesFinalized: toBool(parameters.showPricesFinalized),
				dates: {
					from: parameters.fromDate,
					to: parameters.toDate,
				},
			}

			const res = await purchaseOrderQuery.fetch({
				variables: {
					filter: {
						date: {
							gte: filters.dates.from ? new Date(filters.dates.from) : undefined,
							lte: filters.dates.to ? new Date(filters.dates.to) : undefined,
						},
						pricesFinalized: filters.showPricesFinalized ? null : false,
						doneReceiving: filters.showDoneReceiving ? null : false,
						salesperson: { id: filters.userId ? [filters.userId] : undefined },
						storeId: filters.storeId ? [filters.storeId] : undefined,
					},
				},
			})

			return {
				purchaseOrders: res?.data?.purchaseOrders?.items ?? [],
				...filters,
			}
		},
	})
}
